import { useCallback, useEffect, useState } from 'react'
import { Assessment } from './screens/Assessment'
import { Exercise, type OdevTarifi } from './screens/Exercise'
import {
  EgzersizSunucu,
  HaftaSunucu,
  egzersizJetonunuOku,
  haftaJetonunuOku,
  type HaftaOdevi,
} from './api'
import {
  EXERCISE_CODES,
  TASK_ORDER,
  egzersizAdi,
  egzersizAlani,
  type ExerciseCode,
} from '@nirengi/engine'
import { AlanIkonu } from './components/Cerceve'

type Screen = 'home' | 'assessment' | 'exercise' | 'odev' | 'hafta'

export function App() {
  /**
   * Adreste jeton varsa doğrudan açılır.
   *
   * Çocuğun önüne menü koymak gereksiz bir adım: uygulayıcı bağlantıyı
   * zaten o ölçüm ya da o ödev için verdi. Menüde oyalanmak, başlamadan
   * dikkatin dağılması demek.
   *
   *   ?t=…  ölçüm
   *   ?e=…  tek egzersiz ödevi
   *   ?h=…  haftanın tamamı (liste)
   *
   * Haftalık bağlantıda menü YOK ama LİSTE var: tek fark, çocuğun o
   * hafta için verilmiş egzersizler arasından seçmesi. Ödev başına
   * bağlantı üretince her egzersiz için ayrı QR okutmak gerekiyordu.
   */
  const [screen, setScreen] = useState<Screen>(() => {
    const q = new URLSearchParams(location.search)
    if (q.has('t')) return 'assessment'
    if (q.has('e')) return 'odev'
    if (q.has('h')) return 'hafta'
    return 'home'
  })

  if (screen === 'assessment') return <Assessment onExit={() => setScreen('home')} />
  if (screen === 'odev') return <Odev onExit={() => setScreen('home')} />
  if (screen === 'hafta') return <Hafta />
  if (screen === 'exercise') return <Exercise onExit={() => setScreen('home')} />

  return (
    <div className="menu">
      <h1>Nirengi</h1>
      <p>Gösterim sürümü. Hiçbir veri kaydedilmiyor, sunucuya gönderilmiyor.</p>

      <div className="card" onClick={() => setScreen('assessment')}>
        <div className="t">Ölçüm</div>
        {/*
          * Sayılar SAYILIYOR, elle yazılmıyor.
          *
          * "Altı görev" ve "41 şablon" burada sabit yazıyordu; batarya
          * yedinci göreve, katalog 42. şablona çıkınca ikisi de yalan
          * söylemeye başladı ve kimse fark etmedi — bu ekran kimsenin
          * sınamadığı bir yer. Aynı hata ölçüm giriş ekranında da
          * vardı.
          */}
        <div className="d">
          {TASK_ORDER.length} görev: dikkat, seçici dikkat, görsel ve sözel işleyen bellek,
          işlem hızı, esneklik, ritim. Zorluk kendiliğinden ayarlanır.
        </div>
      </div>

      <div className="card" onClick={() => setScreen('exercise')}>
        <div className="t">Egzersizler</div>
        <div className="d">{EXERCISE_CODES.length} şablon; her açılışta yeni sayfa üretilir.</div>
      </div>

      <p className="note" style={{ textAlign: 'left', marginTop: 8 }}>
        Ölçüm için cihazı yatay tutmak ve pencereyi öne almak gerekiyor: arka plandaki
        pencerede tarayıcı kare çizmeyi durdurabiliyor ve süreler bozuluyor.
      </p>
    </div>
  )
}

/**
 * Uygulayıcının verdiği ödev.
 *
 * Sunucu sayfayı değil, sayfayı üretecek olan şeyi gönderiyor: şablon
 * kodu, tohum ve seviye. Aynı tohum aynı sayfayı verdiği için cihaz
 * kendi üretiyor ve puanlarken sunucu da aynısını üretiyor.
 */
function Odev({ onExit }: { onExit: () => void }) {
  const [sunucu] = useState(() => {
    const jeton = egzersizJetonunuOku()
    return jeton ? new EgzersizSunucu(jeton) : null
  })
  const [odev, setOdev] = useState<OdevTarifi | null>(null)
  const [durum, setDurum] = useState<'yukleniyor' | 'hazir' | 'yok' | 'zaten'>('yukleniyor')

  useEffect(() => {
    if (!sunucu) {
      setDurum('yok')
      return
    }
    void sunucu.odev().then((o) => {
      if (!o) {
        setDurum('yok')
        return
      }
      if (o.bitti) {
        setDurum('zaten')
        return
      }
      setOdev({
        id: o.id,
        kod: o.kod as ExerciseCode,
        duzey: o.duzey,
        tohum: o.tohum,
        bitti: o.bitti,
      })
      setDurum('hazir')
    })
  }, [sunucu])

  if (durum === 'yukleniyor') {
    return (
      <div className="menu">
        <p>Hazırlanıyor…</p>
      </div>
    )
  }

  if (durum === 'zaten') {
    return (
      <div className="menu">
        <h1>Bu çalışma yapılmış</h1>
        <p>Bu sayfayı kapatabilirsin.</p>
      </div>
    )
  }

  if (durum === 'yok' || !odev || !sunucu) {
    return (
      <div className="menu">
        <h1>Bağlantı geçersiz</h1>
        <p>
          Bu bağlantının süresi dolmuş ya da daha önce kullanılmış olabilir. Merkezden yeni bir
          bağlantı isteyin.
        </p>
        <button onClick={onExit}>Kapat</button>
      </div>
    )
  }

  return (
    <Exercise
      odev={odev}
      onExit={onExit}
      onGonder={(cevap, sureMs) => sunucu.bitir(cevap, sureMs)}
    />
  )
}

/**
 * Haftanın listesi.
 *
 * Senaryo: uygulayıcı haftalık programı attı, çocuk tableti getirdi,
 * panelde tek bir QR okuttu. Buradan haftanın egzersizlerini
 * sırayla yapıyor; her biri için yeni bir bağlantı gerekmiyor.
 *
 * ÇOCUĞA PUAN GÖSTERİLMİYOR — ürünün geri kalanıyla aynı kural.
 * Yapılmış olan "bitti" diye işaretli, kaç doğru yaptığı değil.
 */
function Hafta() {
  const [sunucu] = useState(() => {
    const jeton = haftaJetonunuOku()
    return jeton ? new HaftaSunucu(jeton) : null
  })
  const [odevler, setOdevler] = useState<HaftaOdevi[] | null>(null)
  const [durum, setDurum] = useState<'yukleniyor' | 'liste' | 'yok'>('yukleniyor')
  const [acilan, setAcilan] = useState<{ odev: OdevTarifi; sunucu: EgzersizSunucu } | null>(null)
  /**
   * Bu oturumda geçen süre.
   *
   * Oturum sonu ekranı "kaç çalışma, kaç dakika" diyor
   * (`dokuman/gorsellik.md` §3) ve buradaki sayı TAHMİN değil, gerçek:
   * her çalışmanın süresi zaten sunucuya gidiyor, aynı sayıyı burada
   * da topluyoruz. Sunucudan tahmini süreyi istemek daha kolaydı ama
   * çocuğa "20 dakika çalıştın" demek, 40 dakika çalıştıysa yanlış.
   *
   * Sayfa yenilenirse sıfırlanıyor: bu bir oturum özeti, bir kayıt
   * değil. Kalıcı olan ne varsa zaten sunucuda.
   */
  const [gecenMs, setGecenMs] = useState(0)
  const [aciliyor, setAciliyor] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    if (!sunucu) {
      setDurum('yok')
      return
    }
    const liste = await sunucu.odevler()
    if (!liste) {
      setDurum('yok')
      return
    }
    setOdevler(liste)
    setDurum('liste')
  }, [sunucu])

  useEffect(() => {
    void yukle()
  }, [yukle])

  /**
   * Seçilen ödevi açar.
   *
   * Hafta jetonu karşılığında O ÖDEVE ait egzersiz jetonu alınıyor;
   * oradan sonrası tek ödevlik akışın aynısı. "Bir jeton = bir
   * egzersiz" kuralı korunuyor.
   */
  const ac = async (o: HaftaOdevi) => {
    if (!sunucu) return
    setHata(null)
    setAciliyor(o.id)
    const jeton = await sunucu.ac(o.id)
    if (!jeton) {
      setHata('Bu çalışma açılamadı. Merkezden yardım isteyebilirsin.')
      setAciliyor(null)
      return
    }
    const es = new EgzersizSunucu(jeton)
    const tarif = await es.odev()
    if (!tarif) {
      setHata('Bu çalışma açılamadı. Merkezden yardım isteyebilirsin.')
      setAciliyor(null)
      return
    }
    setAcilan({
      odev: {
        id: tarif.id,
        kod: tarif.kod as ExerciseCode,
        duzey: tarif.duzey,
        tohum: tarif.tohum,
        bitti: tarif.bitti,
      },
      sunucu: es,
    })
    setAciliyor(null)
  }

  if (acilan) {
    /**
     * Çerçevedeki "3 / 8" listenin kendi sırasından geliyor.
     *
     * Yapılmışlar dışlanmıyor: numara haftanın tamamında sabit kalsın.
     * Kalanlara göre saysaydık çocuk bir çalışmayı bitirdiğinde
     * ötekilerin numarası kayıyordu ve "4. çalışma" iki ayrı şey
     * oluyordu.
     */
    const sira = (odevler ?? []).findIndex((o) => o.id === acilan.odev.id) + 1
    return (
      <Exercise
        odev={acilan.odev}
        listeVar
        sira={sira || undefined}
        toplam={odevler?.length}
        biten={(odevler ?? []).filter((o) => o.yapildi).length + 1}
        onExit={() => {
          setAcilan(null)
          // Listeye dönerken yeniden okunuyor: biten çalışma işaretli görünsün
          void yukle()
        }}
        onGonder={(cevap, sureMs) => {
          setGecenMs((t) => t + sureMs)
          return acilan.sunucu.bitir(cevap, sureMs)
        }}
      />
    )
  }

  if (durum === 'yukleniyor') {
    return (
      <div className="menu">
        <p>Hazırlanıyor…</p>
      </div>
    )
  }

  if (durum === 'yok' || !odevler) {
    return (
      <div className="menu">
        <h1>Bağlantı geçersiz</h1>
        <p>
          Bu bağlantının süresi dolmuş olabilir. Merkezden yeni bir kod okutmanı
          isteyebilirsin.
        </p>
      </div>
    )
  }

  const kalan = odevler.filter((o) => !o.yapildi)
  const biten = odevler.filter((o) => o.yapildi)

  return (
    <div className="menu">
      <h1>Bu haftanın çalışmaları</h1>
      {hata && <p className="note">{hata}</p>}

      {kalan.length === 0 ? (
        /*
         * Oturum sonu: kaç çalışma, kaç dakika. PUAN YOK, yıldız yok,
         * rozet yok (§3). Ödül çocuğu doğru cevaptan hızlı cevaba
         * kaydırıyor ve hız da ölçtüğümüz şeylerden biri; övgüyü
         * uygulayıcı ve veli verir, yazılım saymakla yetinir.
         */
        <div className="oturumSonu">
          <p className="buyuk">Bu haftanın çalışmaları bitti.</p>
          <p>
            <b>{odevler.length}</b> çalışma
            {gecenMs > 0 && (
              <>
                {' · '}
                <b>{Math.max(1, Math.round(gecenMs / 60000))}</b> dakika
              </>
            )}
          </p>
          <p className="note">Bu sayfayı kapatabilirsin.</p>
        </div>
      ) : (
        <p>
          {kalan.length} çalışma kaldı. Sırayla yapabilirsin; istediğin yerden
          başlayabilirsin.
        </p>
      )}

      {kalan.map((o) => (
        <div
          className="card"
          key={o.id}
          onClick={() => {
            if (!aciliyor) void ac(o)
          }}
        >
          {/*
            Alan ikonu listede de duruyor: çocuk "bu hafta dört ayrı şey
            var" bilgisini buradan alıyor, biz de ödev paketine bakınca
            dengeyi tek bakışta görüyoruz. Egzersiz adı tek başına bunu
            vermiyor — "Farkı bul" ile "Eş bulma" aynı şey gibi okunuyor.
          */}
          <div className="t">
            <span className="ikon">
              <AlanIkonu alan={egzersizAlani(o.kod)} />
            </span>
            {egzersizAdi(o.kod)}
          </div>
          <div className="d">{aciliyor === o.id ? 'Açılıyor…' : 'Başlamak için dokun'}</div>
        </div>
      ))}

      {biten.length > 0 && (
        <>
          <p className="note" style={{ textAlign: 'left', marginTop: 14 }}>
            Bitirdiklerin
          </p>
          {biten.map((o) => (
            <div className="card" key={o.id} style={{ opacity: 0.5 }}>
              <div className="t">
                <span className="ikon">
                  <AlanIkonu alan={egzersizAlani(o.kod)} />
                </span>
                {egzersizAdi(o.kod)}
              </div>
              <div className="d">Bitti ✓</div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
